import { useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { GrAttachment } from "react-icons/gr";
import { useParams } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  LoadingOverlay,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useMounted } from "@mantine/hooks";
import { IconSearch, IconX } from "@tabler/icons-react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import { GeneralTabProps } from "@pages/Patients/Laboratory/components/GeneralTab";
import { StyledGeneralTabTable } from "@pages/Patients/Laboratory/components/GeneralTab/GeneralTab.styled";

import { CustomPagination } from "@components/CustomPagination";

import { formatDateToString } from "@utils/dateValidation";
import { truncateWithEllipsis } from "@utils/truncateText";

import { getLabsStatuses } from "@data/dropdownValues";

import { labStatusColors } from "@constants/colors";
import { QP } from "@constants/index";

import { useGetClinicRadiologies } from "@hooks/queries/clinic/useGetClinicRadiologies";
import { useGetRadiologyForPatient } from "@hooks/queries/radiology/useGetRadiologyForPatient";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";

import { Radiology } from "@customTypes/radiology";

import { TableControls } from "../TableControls";


const columnHelper = createColumnHelper<Partial<Radiology>>();

export const GeneralTab = ({ setIsRefreshing }: GeneralTabProps) => {
  const { t, i18n } = useTranslation();
  const { patientId } = useParams();

  const [searchFilter, setSearchFilter] = useQueryParam(
    QP.SEARCH,
    withDefault(StringParam, "")
  );
  const [statusFilter, setStatusFilter] = useQueryParam(
    QP.STATUS,
    withDefault(StringParam, "all")
  );
  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter, statusFilter });
  const [tableData, setTableData] = useState<Partial<Radiology>[] | []>([]);

  const [isMounted, setIsMounted] = useState(true);
  const mounted = useMounted();

  const { data, isError, isSuccess, isLoading, fetchStatus, isRefetching } =
    useGetRadiologyForPatient({
      patientId: patientId as string,
      fastConsultationOnly: false,
      pagination: {
        page: tablePagination.page,
        pageSize: tablePagination.pageSize,
      },
      search: debounced.toLocaleLowerCase(),
      status: statusFilter === "all" ? undefined : statusFilter || "",
    });
  const { data: clinicRadiologyData } = useGetClinicRadiologies();

  const radiologyColumns = useMemo(
    () => [
      columnHelper.accessor("radType", {
        header: t("radiology-type"),
        cell: info => {
          // console.log(info.row.original);
          return (
            <p className="type-wrapper">
              {info.row.original.files?.length ? <GrAttachment /> : null}
              {truncateWithEllipsis(info.cell.getValue(), 20)}
            </p>
          );
        },
      }),
      columnHelper.accessor("date", {
        header: t("date"),
        cell: info => {
          return (
            <Stack gap={5}>
              <Text fz={14}>
                {formatDateToString(info.cell.getValue() as Date)}
              </Text>
              <Text c="dimmed" fz={13}>
                {(info.cell.getValue() as Date).toLocaleTimeString(
                  i18n.language,
                  {
                    hour: "2-digit",
                    minute: "2-digit",
                  }
                )}
              </Text>
            </Stack>
          );
        },
      }),
      columnHelper.accessor("orderedBy", {
        header: t("ordered-by"),
        cell: info => truncateWithEllipsis(info.cell.getValue()?.fullName, 15),
      }),
      columnHelper.accessor("status", {
        header: t("status"),
        cell: info => {
          return (
            <Badge
              fz={12}
              c={labStatusColors.get(info.cell.getValue() as string)?.c}
              bg={labStatusColors.get(info.cell.getValue() as string)?.bg}>
              {t(`${info.cell.getValue()}`)}
            </Badge>
          );
        },
      }),
      columnHelper.display({
        id: "action",
        header: isMobile ? t("action") : t("last-updated-action"),
        cell: info => {
          // console.log(info.row.original);
          return (
            <TableControls
              radTypes={clinicRadiologyData || []}
              radiology={info.row.original || {}}
            />
          );
        },
      }),
    ],
    [tableData, clinicRadiologyData, i18n.language]
  );

  const table = useReactTable({
    data: tableData,
    columns: radiologyColumns,
    // state: {
    //   globalFilter: searchFilter,
    // },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // onGlobalFilterChange: setStatusFilter,
    // globalFilterFn: (
    //   row: Row<Partial<Radiology>>,
    //   columnId: string,
    //   value: string
    // ) => {
    //   if (columnId === "radType") {
    //     return filterFns.includesString(row, columnId, value, () => null);
    //   }
    //   return false;
    // },
  });

  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
    } else {
      setTableData([]);
    }
  }, [data]);

  useEffect(() => {
    if (setIsRefreshing) setIsRefreshing(isRefetching);
    if (mounted && isRefetching) return;
    if (mounted && !isRefetching) {
      setIsMounted(false);
    }
  }, [isRefetching, mounted]);

  return (
    <>
      <LoadingOverlay
        visible={isRefetching && !!data?.newData?.length && !isMounted}
        zIndex={1000}
        overlayProps={{ blur: 0.1, opacity: 0.6 }}
        loaderProps={{ display: "none" }}
      />
      <div className="filters-wrapper">
        <TextInput
          placeholder={t(`search-for-record`)}
          w={{ base: 300, md: 350, lg: 400 }}
          value={searchFilter}
          onChange={e =>
            setSearchFilter(
              e.currentTarget.value.toLocaleLowerCase(),
              "replace"
            )
          }
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchFilter && (
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => setSearchFilter(null)}>
                <IconX size={16} />
              </ActionIcon>
            )
          }
        />

        <div className="dropdown-wrapper">
          <p>{t("filter")}: </p>
          <Select
            w={150}
            data={[{ label: t("all"), value: "all" }, ...getLabsStatuses()]}
            value={statusFilter}
            onChange={val => {
              setStatusFilter(val, "replaceIn");
            }}
            disabled={isLoading || isRefetching}
          />
        </div>
      </div>

      <StyledGeneralTabTable>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {isSuccess && !data?.newData?.length && debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={radiologyColumns.length}>
                <p>
                  {t("empty-results-for", {
                    searchFilter: `"${debounced}"`,
                  })}
                </p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isSuccess && !data?.newData?.length && !debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={radiologyColumns.length}>
                <p>{t("empty-radiologies")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isLoading && fetchStatus === "fetching" && !tableData.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={radiologyColumns.length}>
                <p>{t("loading")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isError && !data?.newData?.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={radiologyColumns.length}>
                <p>{t("encountered-error")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr key={row.id}>
              {row.getVisibleCells().map(cell => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </StyledGeneralTabTable>

      {(isLoading && fetchStatus === "fetching") || !data ? null : (
        <CustomPagination
          table={table}
          tablePagination={tablePagination}
          otherParams={{
            ...(debounced && { [QP.SEARCH]: debounced }),
            [QP.TAB]: "general",
            [QP.STATUS]: statusFilter,
          }}
        />
      )}
    </>
  );
};
